import { BookX, UserRound } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext, useParams, useSearchParams } from 'react-router';
import { EntityFacts, EntityHead, RelatedSection } from '@/components/entity-sheet/EntityParts';
import { EntitySheet } from '@/components/entity-sheet/EntitySheet';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types';
import { apiPlace, sectionPath } from '../place/place';
import { useRoleText } from '../roles/role-text';
import { StateMessage } from '../ui/StateMessage';
import { assignedBy, groupOf, type RoleCatalogContext } from './RoleCatalogPage';
import type { RoleCatalogEntry } from './use-role-catalog';

type Assignment = components['schemas']['AdministrationRoleAssignmentResponse'];
/** «Chi lo ha qui» shows at most this many people; «Vedi tutti» leads to the full list. */
const HOLDERS_SHOWN = 5;

/**
 * A role's card (decisions 100 and 101): what it allows, its module, where it is granted and who
 * may assign it, then who holds it here, linking to «Ruoli e accessi» filtered on the role. It
 * reads from the catalog it was opened from.
 *
 * Decision 100's two switches (which member tags the role admits, org and ext, for the Platform
 * admin) belong in their own section after the facts. They wait for platform's read and write of
 * the tags, and are not drawn until then.
 */
export function RoleSheet() {
  const { t } = useTranslation('administration');
  const context = useOutletContext<RoleCatalogContext>();
  const { place, items, loading } = context;
  const { roleId = '' } = useParams();
  const [params] = useSearchParams();
  const titleId = useId();
  const { name } = useRoleText();
  const query = params.size > 0 ? `?${params.toString()}` : '';
  const listPath = sectionPath(place, 'role-catalog');
  const back = `${listPath}${query}`;
  const index = items.findIndex((entry) => entry.id === roleId);
  const entry = index >= 0 ? items[index] : undefined;
  const neighbour = (other: RoleCatalogEntry | undefined, key: 'previous' | 'next') =>
    other === undefined
      ? null
      : {
          to: `${listPath}/${other.id}${query}`,
          label: t(`roleCatalog.card.${key}`, { role: name(other) }),
        };

  return (
    <EntitySheet
      titleId={titleId}
      back={back}
      backLabel={t('roleCatalog.card.back')}
      closeLabel={t('roleCatalog.card.close')}
      previous={neighbour(items[index - 1], 'previous')}
      next={index >= 0 ? neighbour(items[index + 1], 'next') : null}
    >
      {entry !== undefined ? (
        <RoleCard entry={entry} titleId={titleId} context={context} />
      ) : (
        <GoneOrLoading loading={loading} titleId={titleId} back={back} />
      )}
    </EntitySheet>
  );
}

function GoneOrLoading({
  loading,
  titleId,
  back,
}: {
  readonly loading: boolean;
  readonly titleId: string;
  readonly back: string;
}) {
  const { t } = useTranslation('administration');
  if (loading) {
    return (
      <div>
        <h2 id={titleId} className="sr-only">
          {t('common.loading')}
        </h2>
        <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-3">
          <Skeleton className="h-12 w-60" />
          <Skeleton className="h-32 w-full" />
        </SkeletonRegion>
      </div>
    );
  }
  return (
    <div>
      <h2 id={titleId} className="sr-only">
        {t('roleCatalog.card.gone.title')}
      </h2>
      <StateMessage
        role="alert"
        icon={BookX}
        title={t('roleCatalog.card.gone.title')}
        text={t('roleCatalog.card.gone.text')}
        action={
          <Button asChild variant="outline">
            <Link to={back}>{t('roleCatalog.card.back')}</Link>
          </Button>
        }
      />
    </div>
  );
}

function RoleCard({
  entry,
  titleId,
  context,
}: {
  readonly entry: RoleCatalogEntry;
  readonly titleId: string;
  readonly context: RoleCatalogContext;
}) {
  const { t, i18n } = useTranslation('administration');
  const { name, description } = useRoleText();
  const { place, sections } = context;
  const pair = apiPlace(place);
  const holders = useHolders(pair?.placeKind, pair?.placeId, entry.id);
  const moduleKey = `administration:modules.${groupOf(entry)}`;
  const moduleName = i18n.exists(moduleKey)
    ? t(moduleKey as never)
    : (entry.module?.name ?? t('roleCatalog.card.noModule'));
  const rolesPath = sections.includes('roles') ? sectionPath(place, 'roles') : null;
  const peoplePath = sections.includes('people') ? sectionPath(place, 'people') : null;

  return (
    <div className="flex flex-col gap-4">
      <EntityHead
        titleId={titleId}
        kind={t('roleCatalog.card.kind')}
        title={name(entry)}
        sub={description(entry) ?? t('roleCatalog.noDescription')}
        status={<span className="status-badge info">{t(`roleCatalog.scope.${entry.scope}`)}</span>}
      />
      <EntityFacts
        facts={[
          { label: t('roleCatalog.card.facts.module'), value: moduleName },
          {
            label: t('roleCatalog.card.facts.scope'),
            value: t(`roleCatalog.scope.${entry.scope}`),
          },
          {
            label: t('roleCatalog.card.facts.assignedBy'),
            value: assignedBy(t, entry.assignableBy),
          },
        ]}
      />
      {holders === null ? null : (
        <RelatedSection
          id={`${titleId}-holders`}
          title={t('roleCatalog.card.holders', { place: context.detail.place.name })}
          count={holders.total}
          rows={holders.items.slice(0, HOLDERS_SHOWN).map((assignment) => ({
            key: assignment.id,
            to:
              peoplePath === null ? '#' : `${peoplePath}/${encodeURIComponent(assignment.user.id)}`,
            label: assignment.user.displayName,
            sub:
              assignment.via === 'direct'
                ? t('roleCatalog.card.direct')
                : t('roleCatalog.card.inherited', {
                    via: assignment.viaName ?? t('roleCatalog.card.viaUnknown'),
                  }),
            lead: (
              <UserRound aria-hidden="true" className="text-muted-foreground size-4.5 flex-none" />
            ),
          }))}
          {...(rolesPath === null
            ? {}
            : {
                more: {
                  to: `${rolesPath}?role=${encodeURIComponent(entry.id)}`,
                  label: t('roleCatalog.card.seeAll'),
                },
              })}
        />
      )}
    </div>
  );
}

/**
 * Who holds the role at this place: the first page of «Ruoli e accessi» filtered on it. None at the
 * platform, where the catalog is read but nobody is granted «here»; `null` until it answers, and
 * on a refusal (the card then shows the role without the list).
 */
function useHolders(
  placeKind: 'tenants' | 'organizations' | undefined,
  placeId: string | undefined,
  roleId: string,
): { readonly items: readonly Assignment[]; readonly total: number } | null {
  const [holders, setHolders] = useState<{
    readonly items: readonly Assignment[];
    readonly total: number;
  } | null>(null);
  useEffect(() => {
    setHolders(null);
    if (placeKind === undefined || placeId === undefined) {
      return;
    }
    let cancelled = false;
    void apiClient
      .GET('/v1/administration/{placeKind}/{placeId}/role-assignments', {
        params: { path: { placeKind, placeId }, query: { roleId, sort: 'person' } },
      })
      .then(
        ({ data, response }) => {
          if (!cancelled && data !== undefined && response.ok) {
            setHolders({ items: data.items, total: data.total });
          }
        },
        () => undefined,
      );
    return () => {
      cancelled = true;
    };
  }, [placeKind, placeId, roleId]);
  return holders;
}
